import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { z } from 'zod';
import { useTRPC } from '../trpc.js';
import { useToast } from '../ui/Toast.js';

const sheetScope = z.union([
  z.object({ listId: z.string() }),
  z.object({
    grid: z.object({
      fundIds: z.array(z.string()),
      columns: z.array(z.object({ key: z.string(), header: z.string() })),
    }),
  }),
]);

/** What `exports.toSheet` takes as its scope: a list, or the grid's view of the funds. */
export type SheetScope = z.infer<typeof sheetScope>;

/**
 * An export waiting on the person's consent at Google. Kept in the tab's
 * session storage across the round trip, so the export finishes on return
 * with the rows they had, even though the page's filters did not survive it.
 */
const pendingExport = z.object({ path: z.string(), scope: sheetScope });

type Pending = z.infer<typeof pendingExport>;

const PENDING_KEY = 'nemo.pendingSheetExport';

/** What the consent's callback says on the way back. */
const DRIVE_PARAMS = ['drive', 'drive_error'] as const;

const DRIVE_ERRORS: Record<string, string> = {
  denied: 'Google Drive was not connected, so no sheet was made.',
  wrong_account:
    'That Google account is not the one you are signed in with. Choose your own account to export.',
  scope_missing: 'The permission to create files in your Drive was unticked, so no sheet was made.',
  no_refresh_token: 'Google did not hand over a lasting permission. Try exporting again.',
  expired: 'The Google Drive connection took too long. Try exporting again.',
  session: 'You were signed out during the Google Drive connection. Sign in and try again.',
  exchange: 'Google Drive could not be connected. Try exporting again.',
};

function here(): string {
  const url = new URL(window.location.href);
  for (const name of DRIVE_PARAMS) url.searchParams.delete(name);
  return `${url.pathname}${url.search}`;
}

function readPending(): Pending | null {
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    window.sessionStorage.removeItem(PENDING_KEY);
    if (raw === null) return null;
    const parsed = pendingExport.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

function writePending(pending: Pending): void {
  try {
    window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    // Without storage the person lands back on the page and presses again.
  }
}

/**
 * Export to a new Google Sheet. The first time, the person is sent to Google
 * to allow it and brought back to the same page, where the export completes.
 */
export function useSheetExport(): {
  exportToSheet: (scope: SheetScope | Promise<SheetScope>) => void;
  pending: boolean;
} {
  const trpc = useTRPC();
  const toast = useToast();
  const toSheet = useMutation(trpc.exports.toSheet.mutationOptions());
  const resumed = useRef(false);

  const run = useCallback(
    (scope: SheetScope, tab: Window | null) => {
      toSheet.mutate(
        { scope, returnTo: here() },
        {
          onSuccess: (answer) => {
            if (answer.needsGrant) {
              tab?.close();
              writePending({ path: here(), scope });
              window.location.assign(answer.authUrl);
              return;
            }
            if (tab !== null) {
              tab.location.href = answer.url;
              return;
            }
            // No tab to put it in (a pop-up blocker, or the export resumed
            // after Google with no click to open one): offer the link.
            toast('The sheet is ready.', {
              tone: 'info',
              action: { label: 'Open sheet', run: () => window.open(answer.url, '_blank') },
            });
          },
          onError: (error) => {
            tab?.close();
            toast(error.message || 'The sheet could not be made.');
          },
        },
      );
    },
    [toSheet, toast],
  );

  // Back from Google: finish the export that was waiting, or say why not.
  useEffect(() => {
    if (resumed.current) return;
    const url = new URL(window.location.href);
    const connected = url.searchParams.get('drive') === 'connected';
    const failed = url.searchParams.get('drive_error');
    if (!connected && failed === null) return;
    resumed.current = true;
    for (const name of DRIVE_PARAMS) url.searchParams.delete(name);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}`);
    const pending = readPending();
    if (failed !== null) {
      toast(DRIVE_ERRORS[failed] ?? 'Google Drive could not be connected.');
      return;
    }
    if (pending !== null && pending.path === here()) run(pending.scope, null);
  }, [run, toast]);

  const exportToSheet = useCallback(
    (scope: SheetScope | Promise<SheetScope>) => {
      // Opened inside the click, which is what lets a browser allow it; the
      // sheet's address is filled in once Google has made it.
      const tab = window.open('', '_blank');
      if (tab !== null) tab.document.title = 'Creating the sheet…';
      Promise.resolve(scope)
        .then((resolved) => run(resolved, tab))
        .catch((error: unknown) => {
          tab?.close();
          toast(error instanceof Error ? error.message : 'Could not read the funds to export.');
        });
    },
    [run, toast],
  );

  return { exportToSheet, pending: toSheet.isPending };
}
